import React, { useContext, useEffect, useState } from "react";
import { Alert, Table } from "../../layout";
import { Avatar, Box, IconButton, Tooltip, Button } from "@mui/material";
import { EditRounded, VisibilityRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../App";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { action, controller, Server } from "../../api/Server";

const Customers = (props) => {
  const navigate = useNavigate();
  const { setLoader, settings, setPageTitle, authUser } =
    useContext(AppContext);
  const { t } = useTranslation();
  const [tblData, setTblData] = useState([]);
  const [csvHeader, setCsvHeader] = useState([]);
  const [access, setAccess] = useState({});
  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  useEffect(() => {
    if (authUser && authUser.featuresAccess && authUser.featuresAccess.customer)
      setAccess({
        ...authUser.featuresAccess.customer,
        isHaveDueSaleAccess: authUser.featuresAccess?.pos.isHaveDueSaleAccess,
      });
    return () => {};
  }, [authUser]);

  useEffect(() => {
    setPageTitle && setPageTitle(t("customers"));
    setCsvHeader([
      { key: "cstmrCode", label: t("customerCode") },
      { key: "fullName", label: t("fullName") },
      { key: "phone", label: t("phone") },
      { key: "email", label: t("email") },
      ...(access.isHaveDueSaleAccess
        ? [{ key: "storeCredit", label: t("storeCredit") }]
        : []),
      { key: "hasCard", label: t("hasCard") },
    ]);

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoader(true);
    const onComplete = (res) => {
      setLoader(false);
      res && res.status === 200 && setTblData([...res.result]);
      setLoader(false);
    };

    Server(controller.Customer + action.GetAllByOrg).get(onComplete);
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  const handleAddEdit = (id) => {
    navigate(`customer-form/${id || ""}`);
  };

  const handleView = (id) => {
    navigate(`/view-customer/${id}`);
  };

  const ActionComp = (val) => (
    <div className="bs-secondary br-8 w-fit">
      {access.isHaveViewAccess && (
        <Tooltip title="View">
          <IconButton
            onClick={() => handleView(val.id)}
            size="small"
            color="primary"
            className="mr-5 p-5"
          >
            <VisibilityRounded className="fs-18 m-2" />
          </IconButton>
        </Tooltip>
      )}
      {access.isHaveEditAccess && (
        <Tooltip title="Edit">
          <IconButton
            onClick={() => handleAddEdit(val.id)}
            size="small"
            color="primary"
            className="mr-5 p-5"
          >
            <EditRounded className="fs-18 m-2" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
  const HeaderComp = () => (
    <div className="d-flex flex-wrap g-5">
      {access.isHaveAddAccess && (
        <Button
          onClick={() => handleAddEdit()}
          className="btn-secondary"
          sx={{
            width: { md: "180px", xs: "100%" },
            mb: { xs: "10px", md: "0px" },
            fontSize: "17px",
            padding: "8px",
          }}
        >
          {t("addCustomer")}
        </Button>
      )}
      {access.isHaveExportAccess && (
        <Box
          className="d-flex"
          sx={{
            width: { xs: "100%", md: "auto" },
          }}
        >
          <CSVLink
            data={tblData.map((j) => ({
              ...j,
              hasCard: j.cardNumber ? t("yes") : t("no"),
            }))}
            headers={csvHeader}
            filename={`${t("customers")} (${new Date().toDateString()}).csv`}
            target="_blank"
            className="d-flex a-center j-center p-15 fs-16 fw-5 tx-upp primary-color bg-primarySM br-15"
          >
            {t("exportCSV")}
          </CSVLink>
        </Box>
      )}
    </div>
  );

  return (
    <>
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={[
          { id: "actions", label: t("actions") },
          { id: "cstmr", label: t("customer") },
          { id: "contact", label: t("contact") },
          ...(access.isHaveDueSaleAccess
            ? [{ id: "creditAndDebit", label: t("storeCredit") }]
            : []),
          { id: "hasCard", label: t("hasCard") },
        ]}
        tblData={tblData.map((j) => {
          return {
            ...j,
            idSearch: `id ${j.id}`,
            creditAndDebit: (
              <div className="fs-14 tx-upp d-grid">
                <div className="success-color">
                  <span>{t("credit")}:&nbsp;</span>
                  <span className="fw-6">
                    {settings.curSymbol +
                      " " +
                      j.creditBal.toFixed(settings.decimal)}
                  </span>
                </div>
                <div className={j.due > 0 ? "error-color" : "gray-color"}>
                  <span>{t("debit")}:</span>
                  <span className="fw-6">
                    &nbsp;
                    {settings.curSymbol + " " + j.due.toFixed(settings.decimal)}
                  </span>
                </div>
              </div>
            ),
            cstmr: (
              <div className="d-flex g-15 a-center mb-5 mt-5">
                <Avatar className="bg-primary" src={j.photo} />
                <div className="d-grid">
                  <span className="">{j.fullName}</span>
                  <span className="fs-12">
                    <span className="fw-5">
                      {t("customerCode")}: {j.cstmrCode}
                    </span>
                    &nbsp;&nbsp; | &nbsp;&nbsp;
                    <span className="fw-5">
                      {t("id")}: {j.id}
                    </span>
                  </span>
                </div>
              </div>
            ),
            credit: (
              <span className="fs-18 fw-5">
                {settings &&
                  settings.curSymbol +
                    " " +
                    parseFloat(j.creditBal.toFixed(settings.decimal))}
              </span>
            ),
            contact: (
              <div className="d-grid ">
                {j.email && (
                  <a
                    className="fs-14 primary-color c-pointer mb-3"
                    href={`mailto:${j.email}`}
                  >
                    ✉️ {j.email}
                  </a>
                )}
                <a
                  className="fs-14 primary-color c-pointer"
                  href={`tel:${j.phone}`}
                >
                  📞 {j.phone}
                </a>
              </div>
            ),
            hasCard: (
              <span className="fw-5">{j.cardNumber ? t("yes") : t("no")}</span>
            ),
          };
        })}
      />

      {/* Alert  */}
      <Alert alert={alert} setAlert={setAlert} />
    </>
  );
};

export default Customers;
